import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Box, Button, Chip, LinearProgress, Link as MuiLink, Paper, Stack, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ApiService from '../network/API';

const PREP_VISIBLE = 4;

const formatDue = (iso) =>
    new Date(iso).toLocaleString('ru-RU', {timeZone: 'Europe/Samara', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'});

const dueText = (l) => (!l.closesAt ? '' : l.closed ? `срок истёк ${formatDue(l.closesAt)}` : `до ${formatDue(l.closesAt)}`);

const EXAM_STATE = {NOT_STARTED: 'ещё не начат', ACTIVE: 'идёт', FINISHED: 'завершён'};

// Карточка блока: цветная иконка, заголовок, необязательный значок справа и содержимое.
const Section = ({icon, color, title, badge, children}) => (
    <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0}}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
                width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                bgcolor: (t) => alpha(t.palette[color].main, 0.16), color: `${color}.main`,
            }}>
                {icon}
            </Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{flex: 1}}>{title}</Typography>
            {badge}
        </Stack>
        {children}
    </Paper>
);

// Предмет глазами студента: часы по расписанию, допуск к экзамену, экзамен и подготовка — карточками в одной сетке.
// Каждая карточка показывается, только если для неё есть данные.
const SubjectDashboard = ({tagId}) => {
    const navigate = useNavigate();
    const [hours, setHours] = useState(null);
    const [admission, setAdmission] = useState(null);
    const [prep, setPrep] = useState([]);
    const [exams, setExams] = useState([]);
    const [showAllPrep, setShowAllPrep] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem('accessToken') || ApiService.isAdmin()) return;
        ApiService.getSubjectHours(tagId).then((d) => setHours(d && typeof d === 'object' ? d : null)).catch(() => setHours(null));
        ApiService.getMyAdmission(tagId).then(setAdmission).catch(() => setAdmission(null));
        ApiService.getExamPrep(tagId).then(setPrep).catch(() => setPrep([]));
        ApiService.getMyExams(tagId).then(setExams).catch(() => setExams([]));
    }, [tagId]);

    const hasAdmission = admission && admission.configured;
    const hasHours = hours && (hours.totalHours != null || hours.hours > 0);
    if (!hasHours && !hasAdmission && prep.length === 0 && exams.length === 0) return null;

    const admitted = hasAdmission && admission.status === 'ADMITTED';
    const planned = hasHours && hours.totalHours != null && hours.totalHours > 0;
    const hoursPct = planned ? Math.min(100, Math.round((hours.hours / hours.totalHours) * 100)) : 0;
    const admissionPct = hasAdmission && admission.required > 0 ? Math.round((admission.passed / admission.required) * 100) : 0;
    const shownPrep = showAllPrep ? prep : prep.slice(0, PREP_VISIBLE);

    return (
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, minmax(0, 1fr))'}, gap: 2, mb: 3}}>
            {hasHours && (
                <Section icon={<ScheduleOutlinedIcon/>} color="primary" title="Часы по предмету"
                         badge={planned && (
                             <Chip size="small" color={hours.remaining < 0 ? 'error' : 'default'} variant="outlined"
                                   label={hours.remaining >= 0 ? `осталось ${hours.remaining} ч` : `сверх плана ${-hours.remaining} ч`}/>
                         )}>
                    <Box>
                        <Typography variant="h5" fontWeight={700} component="span">{hours.hours}</Typography>
                        <Typography variant="body1" color="text.secondary" component="span">{planned ? ` из ${hours.totalHours} ч` : ' ч'}</Typography>
                    </Box>
                    {planned && <LinearProgress variant="determinate" value={hoursPct} sx={{height: 8, borderRadius: 4}}/>}
                    <Typography variant="body2" color="text.secondary">
                        {hours.lessons} пар проведено{planned ? ` (${hoursPct}% плана)` : ''}. {hours.source === 'JOURNAL' ? 'По журналу АСУ РСО.' : 'По расписанию.'} Одна пара = 2 часа.
                    </Typography>
                </Section>
            )}

            {hasAdmission && (
                <Section icon={<VerifiedUserOutlinedIcon/>} color={admitted ? 'success' : 'warning'} title="Допуск к экзамену"
                         badge={<Chip size="small" color={admitted ? 'success' : 'warning'} label={admitted ? 'допущен' : 'нет допуска'}/>}>
                    <Box>
                        <Typography variant="h5" fontWeight={700} component="span">{admission.passed}</Typography>
                        <Typography variant="body1" color="text.secondary" component="span"> из {admission.required} лекций сдано на 3+</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={admissionPct} color={admitted ? 'success' : 'warning'} sx={{height: 8, borderRadius: 4}}/>
                    {admission.manual === true && <Typography variant="body2" color="text.secondary">Допуск выдан преподавателем.</Typography>}
                    {admission.manual === false && <Typography variant="body2" color="text.secondary">Преподаватель пока не допускает вас к экзамену.</Typography>}
                    {admission.missing.length > 0 && (
                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{mb: 0.5}}>
                                {admitted ? 'Не сданы (допуск выдан вручную):' : 'Чтобы получить допуск, сдайте:'}
                            </Typography>
                            <Stack spacing={0.75}>
                                {admission.missing.map((l) => (
                                    <Stack key={l.articleId} direction="row" alignItems="flex-start" spacing={1}>
                                        <RadioButtonUncheckedIcon sx={{fontSize: 18, mt: '3px', color: 'text.disabled'}}/>
                                        <MuiLink component={Link} to={`/articles/${l.articleId}`} underline="hover" variant="body2" sx={{flex: 1, minWidth: 0}}>
                                            {l.title}
                                        </MuiLink>
                                        {l.closesAt && (
                                            <Typography variant="caption" color={l.closed ? 'error' : 'text.secondary'} sx={{whiteSpace: 'nowrap', mt: '2px'}}>
                                                {dueText(l)}
                                            </Typography>
                                        )}
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Section>
            )}

            {exams.length > 0 && (
                <Section icon={<AssignmentOutlinedIcon/>} color="info" title={exams.length > 1 ? 'Экзамены' : 'Экзамен'}>
                    <Stack spacing={1.5} divider={<Box sx={{borderTop: 1, borderColor: 'divider'}}/>}>
                        {exams.map((ex) => (
                            <Box key={ex.eventId}>
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Typography variant="body1" fontWeight={600} sx={{flex: 1, minWidth: 160}}>{ex.title}</Typography>
                                    {ex.attempt?.grade != null ? (
                                        <Chip size="small" color="primary" label={`оценка ${ex.attempt.grade}`}/>
                                    ) : (
                                        <Chip size="small" variant="outlined" color={ex.state === 'ACTIVE' ? 'primary' : 'default'} label={EXAM_STATE[ex.state] || ex.state}/>
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                    {ex.state === 'NOT_STARTED' && `Длительность ${ex.durationMinutes} мин. Преподаватель откроет доступ и выдаст билет в начале экзамена.`}
                                    {ex.state === 'ACTIVE' && 'Экзамен идёт: билет выдан, время отсчитывается.'}
                                    {ex.state === 'FINISHED' && 'Экзамен завершён.'}
                                </Typography>
                                <Button size="small" variant={ex.state === 'ACTIVE' ? 'contained' : 'outlined'} sx={{mt: 1}}
                                        onClick={() => navigate(`/exams/event/${ex.eventId}`)}>
                                    {ex.state === 'ACTIVE' ? 'К экзамену' : 'Открыть экзамен'}
                                </Button>
                            </Box>
                        ))}
                    </Stack>
                </Section>
            )}

            {prep.length > 0 && (
                <Section icon={<MenuBookOutlinedIcon/>} color="secondary" title="Подготовка к экзамену"
                         badge={<Chip size="small" variant="outlined" label={prep.length}/>}>
                    <Stack spacing={0.75}>
                        {shownPrep.map((l, i) => (
                            <Stack key={l.articleId} direction="row" alignItems="flex-start" spacing={1.25}>
                                <Typography variant="caption" color="text.disabled" sx={{width: 18, textAlign: 'right', mt: '3px'}}>{i + 1}</Typography>
                                <MuiLink component={Link} to={`/articles/${l.articleId}`} underline="hover" variant="body2">{l.title}</MuiLink>
                            </Stack>
                        ))}
                    </Stack>
                    {prep.length > PREP_VISIBLE && (
                        <Button size="small" onClick={() => setShowAllPrep((v) => !v)} sx={{alignSelf: 'flex-start'}}>
                            {showAllPrep ? 'Свернуть' : `Показать все (${prep.length})`}
                        </Button>
                    )}
                </Section>
            )}
        </Box>
    );
};

export default SubjectDashboard;
